import { PositiveIcon } from "@datagame/ui";
import { motion, useAnimationControls } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import classNames from "classnames/bind";

import { type Attribute, QuickRankMode } from "../../../../gql/graphql";
import {
  CARD_ANIMATION_SEPARATION,
  CARD_TO_DECK_DURATION,
  CARD_TO_DECK_SEPARATION,
  CARD_TO_HAND_DURATION,
  QUICKRANK_REJECTED_CARD_DURATION,
  QUICKRANK_REJECTED_CARD_SCALE,
  QUICKRANK_SELECTED_CARD_DURATION,
  QUICKRANK_SELECTED_CARD_Y,
} from "../../../../constants";
import { CardContent } from "../../../shared/CardContent";
import { QuickRankActionName, QuickRankRenderableDestinations } from "../../../../reducers/quickrank";
import { useQuickRankCardPosition } from "../../../../hooks/quickrank/useQuickRankCardPosition";
import { useQuickRankState } from "../../../../hooks/quickrank/useQuickRankState";
import BrownCard from "../../../../assets/images/cards/brown.png";
import { useGameVisuals } from "../../../../contexts/gameVisuals";

import styles from "./Card.module.scss";

const cx = classNames.bind(styles);

const { HAND, DECK, RATED } = QuickRankRenderableDestinations;

export interface QuickRankCardProps {
  card: {
    index: number;
    attribute: Attribute;
  };
  color?: string;
}

export const Card = ({ card, color }: QuickRankCardProps) => {
  const positions = useQuickRankCardPosition(card);
  const [
    {
      cards: {
        // [HAND]: hand,
        [RATED]: [currentlyRatedCard],
      },
      configuration: {
        mode: gameMode,
        showRatingScale,
        gameVisuals: { cardBackImage, showIcons },
      },
      status: { shouldChangeRound },
    },
    dispatch,
  ] = useQuickRankState();
  const [isSocketedInDeck, setIsSocketedInDeck] = useState(false);
  const [isSocketedOnHand, setIsSocketedOnHand] = useState(false);
  const isRatedMode = currentlyRatedCard?.index === card.index;
  const [isInMotion, setIsInMotion] = useState(false);
  const [exitKind, setExitKind] = useState<"rejected" | "selected">("rejected");
  const controls = useAnimationControls();
  const gameVisuals = useGameVisuals();
  const isOptimizedMode = gameMode === QuickRankMode.Optimized;

  const deckIndex = positions[DECK]?.index ?? 0;
  const handIndex = positions[HAND]?.index ?? 0;

  const cardVariantGenerator = (status: QuickRankRenderableDestinations) => ({
    top: 0,
    left: 0,
    x: positions[status]?.x ?? 0,
    y: positions[status]?.y ?? 0,
    width: positions[status]?.width ?? 0,
    height: positions[status]?.height ?? 0,
  });

  const cardWrapperVariants = {
    [DECK]: {
      ...cardVariantGenerator(DECK),
      transition: {
        duration: CARD_TO_DECK_DURATION,
        delay: deckIndex * CARD_TO_DECK_SEPARATION,
        ease: "linear",
      },
    },
    [HAND]: {
      ...cardVariantGenerator(HAND),
      transition: {
        duration: CARD_TO_HAND_DURATION,
        delay: handIndex * CARD_TO_DECK_SEPARATION + CARD_ANIMATION_SEPARATION,
        ease: "easeInOut",
      },
    },
    [RATED]: {
      ...cardVariantGenerator(RATED),
      transition: {
        duration: CARD_TO_DECK_DURATION,
        ease: "linear",
      },
    },
  };

  const computedZIndex = useMemo(() => {
    if (isInMotion && positions[HAND]) {
      return 3000 - deckIndex;
    }

    if (positions[HAND]) {
      return 2000 - handIndex;
    }

    if (positions[DECK]) {
      return 1000 - deckIndex;
    }

    return 0;
  }, [isInMotion, positions]);

  useEffect(() => {
    positions.deck && controls.start(DECK);
  }, [positions.deck, isSocketedInDeck]);

  useEffect(() => {
    if (positions.hand) {
      isSocketedInDeck && controls.start(HAND);
    } else if (isSocketedOnHand) {
      setIsSocketedOnHand(false);
      controls.start(DECK);
    }
  }, [positions.hand, isSocketedInDeck]);

  useEffect(() => {
    if (!positions[RATED]) {
      return;
    }

    controls.start(RATED);
  }, [positions.rated, isRatedMode]);

  const exitAnimations = {
    selected: {
      y: QUICKRANK_SELECTED_CARD_Y,
      opacity: [1, 0.05, 0],
      // Framer Motion sometimes doesn't remove element from DOM after exit animation
      // this is a workaround to make sure it does not interfere with other elements
      zIndex: [10000, 10000, 0],
      transition: {
        duration: QUICKRANK_SELECTED_CARD_DURATION,
        times: [0, 0.95, 1],
      },
    },
    rejected: {
      scale: QUICKRANK_REJECTED_CARD_SCALE,
      opacity: 0,
      transition: {
        duration: QUICKRANK_REJECTED_CARD_DURATION,
      },
    },
  };

  const handleCardClick = () => {
    if (!positions[HAND]) {
      return;
    }

    setExitKind("selected");
    dispatch({
      type: QuickRankActionName.SELECT_CARD,
      payload: {
        card,
      },
    });
  };

  return (
    <motion.div
      className={cx("card", `quickrank-card-${card.index}-${card.attribute.name}`, {
        twoPickN: gameMode === QuickRankMode.TwoPickN,
        rated: isRatedMode,
      })}
      variants={cardWrapperVariants}
      animate={controls}
      style={{
        zIndex: computedZIndex,
      }}
      onAnimationStart={() => {
        setIsInMotion(true);
      }}
      onAnimationComplete={(definition) => {
        setIsInMotion(false);

        switch (definition) {
          case DECK:
            setIsSocketedInDeck(true);
            break;
          case HAND:
            setIsSocketedOnHand(true);
            break;
        }
      }}
      initial={{
        top: "130%",
        left: "10%",
      }}
      exit={exitAnimations[exitKind]}
    >
      <CardContent
        isReversed={!isSocketedOnHand && !isRatedMode}
        index={handIndex}
        reverseImage={cardBackImage ? cardBackImage.url : BrownCard}
        attribute={card.attribute}
        overlayClass={styles.overlay}
        contentClass={styles.content}
        overlayBackground={`${color}85`}
        isDisabled={shouldChangeRound || isRatedMode}
        shouldLockStylesOnClick={!showRatingScale && !isOptimizedMode}
        icon={showIcons ? <PositiveIcon iconSetKey={gameVisuals?.iconSet} /> : undefined}
        onClick={handleCardClick}
        // onLongPress={() => {
        //   if (!positions[HAND]) {
        //     return;
        //   }

        //   dispatch({
        //     type: QuickRankActionName.DISPLAY_CARD,
        //     payload: {
        //       index: hand.indexOf(card),
        //     },
        //   });
        // }}
      />
    </motion.div>
  );
};
